import React, { useCallback, useEffect, useMemo } from 'react';
import { ListActionSelect } from '@sweetgreen/garnish';

import { SingleOptionCreditCardPicker } from '@order/components';
import { BRAINTREE_CREDIT_CARDS_ICONS } from '@order/constants';
import type { SubscriptionCreditCard } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const SweetpassCheckoutCardPicker = (
  props: SweetpassCheckoutCardPickerProps,
) => {
  const {
    selectedCreditCard,
    creditCards,
    handleAddCreditCard,
    handleSelectCreditCard,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const creditCardIcon = selectedCreditCard?.cardType
    ? BRAINTREE_CREDIT_CARDS_ICONS[selectedCreditCard.cardType]
    : undefined;

  // ─── Credit Card Options ─────────────────────────────────────────────

  const creditCardOptions = useCreditCardOptions(creditCards);

  // ─── Automatically Select First Credit Card If None Are Selected ─────

  useEffect(() => {
    if (!creditCards[0]) return;
    handleSelectCreditCard(creditCards[0]);
  }, [creditCards, handleSelectCreditCard]);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handleSelectedCardChange = useCallback(
    (cardId: string | number) => {
      const creditCard = creditCards.find((card) => card.id === cardId);

      if (!creditCard) {
        handleAddCreditCard();

        return;
      }

      handleSelectCreditCard(creditCard);
    },
    [creditCards, handleSelectCreditCard, handleAddCreditCard],
  );

  // ─── Only Add Card Option ────────────────────────────────────────────

  if (creditCards.length === 0) {
    return <SingleOptionCreditCardPicker onPress={handleAddCreditCard} />;
  }

  // ─── Multiple Credit Card Options ────────────────────────────────────

  return (
    <ListActionSelect
      noticePalette="caution"
      submitBtnLabel={t('checkout.card-picker.submit')}
      iconName={creditCardIcon}
      value={selectedCreditCard?.id ?? 'ADD'}
      options={creditCardOptions}
      onSelect={handleSelectedCardChange}
    />
  );
};

// ─── Hooks ─────────────────────────────────────────────────────────────

const useCreditCardOptions = (
  creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>,
) => {
  const { t } = useLocalizationContext();

  const convertCreditCardIntoOption = useCallback(
    (creditCard: Partial<SubscriptionCreditCard>): CreditCardOption => ({
      value: creditCard.id ?? '',
      label: t('sweetpass.checkout.credit-card.description', {
        cardType: creditCard.cardType,
        lastFour: creditCard.lastFour,
      }),
    }),
    [t],
  );

  const creditCardOptions: readonly CreditCardOption[] = useMemo(
    () => [
      { label: t('checkout.add-card'), value: 'ADD' },
      ...creditCards.map(convertCreditCardIntoOption),
    ],
    [creditCards, convertCreditCardIntoOption, t],
  );

  return creditCardOptions;
};

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardOption = {
  readonly label: string;
  readonly value: string;
};

type SweetpassCheckoutCardPickerProps = {
  readonly selectedCreditCard?: Partial<SubscriptionCreditCard>;
  readonly creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
  readonly handleAddCreditCard: () => void;
  readonly handleSelectCreditCard: (
    creditCard: Partial<SubscriptionCreditCard>,
  ) => void;
};
